import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import VendorList from 'features/Vendors/components/VendorList'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import Layout from 'components/Layout'
import Spinner from 'components/Spinner'
import { Input } from 'components/ui/input'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { ViewToggle } from 'components/ViewToggle'
import SidebarEvaluationsRecent from 'components/Sidebars/SidebarEvaluationsRecent'
import { EvaluationCardCN, STATUS_COLORS } from 'components/Card/composed/EvaluationCardCN'
import { CARD_GRID_CLASS } from 'components/Card/cardGrid'
import {
	ClipboardCheck,
	ChevronRight,
	ChevronUp,
	ChevronDown,
	ArrowUpDown,
	LayoutGrid,
	List,
	Handshake,
} from 'lucide-react'
import { cn } from 'lib/utils'
import useCapabilities from 'hooks/useCapabilities'
import useMediaQuery from 'hooks/useMediaQuery'
import * as _ from 'styles/helpers'
import routes from 'constants/routes'
import { initializeEvaluations, createEvaluation } from 'slices/evaluationSlice'
import { initializePortfolios } from 'slices/portfolioSlice'

const STATUSES = ['ALL', 'DRAFT', 'IN_ANALYSIS', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ARCHIVED']

// Outcome of the last create attempt. A union rather than a set of booleans so
// the mutually exclusive banners can never render together.
type CreateState =
	| { kind: 'idle' }
	| { kind: 'submitting' }
	| { kind: 'conflict'; evaluationId: string; evaluationName?: string }
	| { kind: 'error'; message: string }
	| { kind: 'seedWarning'; evaluationId: string; message: string }

const VIEW_MODE_KEY = 'mdt.evaluations.viewMode'

const CARD_SORT_FIELDS: { id: 'name' | 'updatedAt' | 'status'; label: string }[] = [
	{ id: 'name', label: 'Name' },
	{ id: 'updatedAt', label: 'Date Updated' },
	{ id: 'status', label: 'Status' },
]

type CardSortField = (typeof CARD_SORT_FIELDS)[number]['id']
type CardSortOrder = 'asc' | 'desc'

const Evaluations_IndexCN = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const isMobile = useMediaQuery(_.MQ.before_nav)
	// Community accounts work in evaluations shared with them but cannot start
	// one (MYD-633). The server refuses with 403 CREATE_NOT_ALLOWED either way;
	// this only spares them a button that cannot work.
	const { canCreateAssets } = useCapabilities()
	const searchInputRef = useRef<HTMLInputElement>(null)
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [statusFilter, setStatusFilter] = useState('ALL')
	const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
		const stored = localStorage.getItem(VIEW_MODE_KEY)
		return stored === 'list' ? 'list' : 'cards'
	})
	const [createOpen, setCreateOpen] = useState(false)
	const [newName, setNewName] = useState('')
	const [newDescription, setNewDescription] = useState('')
	const [newMarketArea, setNewMarketArea] = useState('')
	const [newPortfolioId, setNewPortfolioId] = useState('')
	const [createState, setCreateState] = useState<CreateState>({ kind: 'idle' })
	const [cardSortBy, setCardSortBy] = useState<CardSortField>('updatedAt')
	const [cardSortOrder, setCardSortOrder] = useState<CardSortOrder>('desc')

	const handleSearchAll = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value)
	}

	const handleCardSort = useCallback((fieldId: CardSortField) => {
		setCardSortBy(prev => {
			if (prev === fieldId) {
				setCardSortOrder(o => (o === 'asc' ? 'desc' : 'asc'))
				return prev
			}
			setCardSortOrder('asc')
			return fieldId
		})
	}, [])

	const { entities, ids, isLoading, isInitialized } = useSelector((state: any) => state.evaluations)

	useEffect(() => {
		localStorage.setItem(VIEW_MODE_KEY, viewMode)
	}, [viewMode])

	useEffect(() => {
		if (!isInitialized) {
			// @ts-ignore
			dispatch(initializeEvaluations())
		}
	}, [dispatch, isInitialized])

	const evaluations = useMemo(() => ids.map((id: string) => entities[id]), [ids, entities])

	// Portfolio options for the create dialog. Fetched lazily on first open,
	// guarded by a ref rather than the slice's `isInitialized`: several unrelated
	// portfolio mutations reset that flag, so an effect keyed on it can re-fire.
	// Lazy also keeps the heavy sharedportfolios payload off this page's load.
	const { entities: portfolioEntities, ids: portfolioIds } = useSelector(
		(state: any) => state.portfolios,
	)
	const portfoliosRequested = useRef(false)

	useEffect(() => {
		if (!createOpen || portfoliosRequested.current) return
		portfoliosRequested.current = true
		// @ts-ignore
		dispatch(initializePortfolios())
	}, [createOpen, dispatch])

	const portfolios = useMemo(
		() => portfolioIds.map((id: string) => portfolioEntities[id]).filter(Boolean),
		[portfolioIds, portfolioEntities],
	)

	const selectedPortfolio = useMemo(
		() => portfolios.find((p: any) => p._id === newPortfolioId) || null,
		[portfolios, newPortfolioId],
	)
	const selectedPortfolioLocationCount = selectedPortfolio?.locations?.length ?? 0

	const filtered = useMemo(() => {
		let result = evaluations
		if (statusFilter !== 'ALL') {
			result = result.filter((e: any) => e.status === statusFilter)
		}
		if (searchValue.trim()) {
			const q = searchValue.toLowerCase()
			result = result.filter(
				(e: any) =>
					e.name?.toLowerCase().includes(q) || e.marketArea?.toLowerCase().includes(q),
			)
		}
		return result
	}, [evaluations, statusFilter, searchValue])

	const sortedFiltered = useMemo(() => {
		const items = [...filtered]
		items.sort((a: any, b: any) => {
			let aVal: number | string
			let bVal: number | string
			if (cardSortBy === 'updatedAt') {
				aVal = new Date(a?.updatedAt || 0).getTime()
				bVal = new Date(b?.updatedAt || 0).getTime()
			} else {
				aVal = (a?.[cardSortBy] ?? '').toString().toLowerCase()
				bVal = (b?.[cardSortBy] ?? '').toString().toLowerCase()
			}
			if (aVal < bVal) return cardSortOrder === 'asc' ? -1 : 1
			if (aVal > bVal) return cardSortOrder === 'asc' ? 1 : -1
			return 0
		})
		return items
	}, [filtered, cardSortBy, cardSortOrder])

	const resetAndClose = useCallback(() => {
		setCreateOpen(false)
		setNewName('')
		setNewDescription('')
		setNewMarketArea('')
		setNewPortfolioId('')
		setCreateState({ kind: 'idle' })
	}, [])

	const handleCreate = useCallback(async () => {
		if (!newName.trim()) return
		setCreateState({ kind: 'submitting' })

		const portfolioId = newPortfolioId || undefined
		const result: any = await (dispatch as any)(
			createEvaluation({
				name: newName,
				description: newDescription,
				marketArea: newMarketArea,
				// Spread conditionally. An empty string is falsy enough to skip the
				// server's duplicate check, then reaches Evaluation.create and throws
				// a CastError as a 500.
				...(portfolioId ? { portfolio: portfolioId, seedFromPortfolio: true } : {}),
			}),
		)

		if (result?.meta?.requestStatus === 'fulfilled') {
			const created = result.payload
			if (created?.seedWarning) {
				// The evaluation exists but is empty, and the portfolio link is set at
				// creation only. Navigating silently would leave the user on an empty
				// Sites tab with no explanation, so stay put and say what happened.
				setCreateState({
					kind: 'seedWarning',
					evaluationId: created._id,
					message: created.seedWarning,
				})
				return
			}
			resetAndClose()
			navigate(`${routes.EVALUATIONS}/${created._id}`)
			return
		}

		const rejected = result?.payload
		if (rejected?.status === 409 && rejected.existingEvaluationId) {
			setCreateState({
				kind: 'conflict',
				evaluationId: rejected.existingEvaluationId,
				evaluationName: rejected.existingEvaluationName,
			})
			return
		}
		setCreateState({
			kind: 'error',
			message: rejected?.message || 'Failed to create evaluation. Please try again.',
		})
	}, [dispatch, navigate, newName, newDescription, newMarketArea, newPortfolioId, resetAndClose])

	const handleOpenCreated = useCallback(
		(evaluationId: string) => {
			resetAndClose()
			navigate(`${routes.EVALUATIONS}/${evaluationId}`)
		},
		[navigate, resetAndClose],
	)

	const formatDate = (d: string) =>
		new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
					<h2 className="text-2xl font-semibold text-gray-800 mb-4">Evaluations</h2>

					<Layout.PageToolbarFilter
						buttonText={canCreateAssets ? 'New Evaluation' : undefined}
						buttonOnClick={canCreateAssets ? () => setCreateOpen(true) : undefined}
						customButtons={!isMobile ? (
							<div className="flex items-center gap-3">
								{viewMode === 'cards' && (
									<div className="flex items-center gap-1.5 flex-wrap">
										<span className="text-xs text-gray-400 mr-0.5">Sort:</span>
										{CARD_SORT_FIELDS.map(field => {
											const isActive = cardSortBy === field.id
											let SortIcon
											if (!isActive) {
												SortIcon = <ArrowUpDown className="h-3 w-3 text-gray-300" />
											} else if (cardSortOrder === 'asc') {
												SortIcon = <ChevronUp className="h-3 w-3 text-[#0080fe]" />
											} else {
												SortIcon = <ChevronDown className="h-3 w-3 text-[#0080fe]" />
											}
											return (
												<button
													key={field.id}
													type="button"
													onClick={() => handleCardSort(field.id)}
													className={cn(
														'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
														isActive
															? 'bg-[#0080fe]/10 text-[#0080fe]'
															: 'bg-gray-100 text-gray-500 hover:bg-gray-200',
													)}
												>
													{field.label}
													{SortIcon}
												</button>
											)
										})}
									</div>
								)}
								<ViewToggle
									value={viewMode}
									onChange={(v) => setViewMode(v)}
									options={[
										{ value: 'cards' as const, label: 'Cards', icon: LayoutGrid },
										{ value: 'list' as const, label: 'List', icon: List },
									]}
								/>
							</div>
						) : undefined}
						searchInputLabel=""
						searchInputRef={searchInputRef}
						searchInputValue={searchValue}
						searchInputOnChange={handleSearchAll}
					/>

					{/* Status filter pills */}
					<div className="flex items-center gap-1.5 flex-wrap mt-3 mb-5">
						{STATUSES.map((s) => (
							<button
								key={s}
								type="button"
								onClick={() => setStatusFilter(s)}
								className={cn(
									'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
									statusFilter === s
										? 'bg-[#0080fe] text-white'
										: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
								)}
							>
								{s === 'ALL' ? 'All' : s.replace(/_/g, ' ')}
							</button>
						))}
					</div>

					{/* Content */}
					{isLoading ? (
						<Spinner containerHeight="50vh" />
					) : filtered.length === 0 ? (
						<div className="text-center py-20">
							<ClipboardCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
							<p className="text-gray-500 text-sm">
								{evaluations.length === 0
									? canCreateAssets
										? 'No evaluations yet. Create one to get started.'
										: 'No evaluations yet. Evaluations shared with you will appear here.'
									: 'No evaluations match your filters.'}
							</p>
						</div>
					) : viewMode === 'cards' ? (
						<div className={CARD_GRID_CLASS}>
							{sortedFiltered.map((eval_: any) => (
								<EvaluationCardCN
									key={eval_._id}
									name={eval_.name}
									description={eval_.description}
									marketArea={eval_.marketArea}
									status={eval_.status}
									updatedAt={eval_.updatedAt}
									linkUrl={`${routes.EVALUATIONS}/${eval_._id}`}
									siteLocations={(eval_.siteAvatars || []).map((s: any) => ({
										imageUrl: s?.image || null,
										name: s?.name || '',
									}))}
									siteCount={eval_.siteCount}
								/>
							))}
						</div>
					) : (
						<div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Name</TableHead>
										<TableHead className="w-32">Status</TableHead>
										<TableHead className="w-20 text-right">Sites</TableHead>
										<TableHead className="w-40">Last Updated</TableHead>
										<TableHead className="w-12" aria-label="Actions" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedFiltered.map((eval_: any) => (
										<TableRow
											key={eval_._id}
											onClick={() => navigate(`${routes.EVALUATIONS}/${eval_._id}`)}
											className="cursor-pointer"
										>
											<TableCell className="font-medium text-gray-900">{eval_.name}</TableCell>
											<TableCell>
												<Badge className={cn('text-[10px]', STATUS_COLORS[eval_.status])}>
													{eval_.status.replace(/_/g, ' ')}
												</Badge>
											</TableCell>
											<TableCell className="text-right text-gray-600">{eval_.siteCount ?? 0}</TableCell>
											<TableCell className="text-gray-500 text-xs">{formatDate(eval_.updatedAt)}</TableCell>
											<TableCell className="text-right">
												<ChevronRight className="w-4 h-4 text-gray-400 inline" />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</main>

					<RightPanel storageKey="evaluations" label="Evaluations panel">
						<div className="flex flex-col h-1/2 border-b border-gray-200 overflow-y-auto">
							<SidebarEvaluationsRecent />
						</div>
						{/* Vendor Partnerships card — bottom half */}
						<div className="flex flex-col h-1/2">
							<div className="px-4 py-3 border-b border-gray-100">
								<span className="flex items-center gap-2 text-gray-600 text-base font-semibold hover:scale-105 transition-transform duration-150">
									<Handshake className="w-4 h-4" /> Vendor Partnerships
								</span>
							</div>
							<div className="flex-1 overflow-y-auto">
								<VendorList />
							</div>
						</div>
					</RightPanel>
				</div>
			</div>

			{/* Create dialog */}
			<Dialog open={createOpen} onClose={resetAndClose}>
				<DialogContent className="max-w-[700px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<ClipboardCheck className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>New Evaluation</DialogTitle>
								<DialogDescription>Create a new site selection evaluation.</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<div className="space-y-4">
							<div>
								<Label htmlFor="evalName" className="mb-1.5 block">Name *</Label>
								<Input
									id="evalName"
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									placeholder="Q2 2026 Site Selection"
								/>
							</div>
							<div>
								<Label htmlFor="evalDesc" className="mb-1.5 block">Description</Label>
								<Input
									id="evalDesc"
									value={newDescription}
									onChange={(e) => setNewDescription(e.target.value)}
									placeholder="Optional description"
								/>
							</div>
							<div>
								<Label htmlFor="evalMarket" className="mb-1.5 block">Market Area</Label>
								<Input
									id="evalMarket"
									value={newMarketArea}
									onChange={(e) => setNewMarketArea(e.target.value)}
									placeholder="e.g. Greater Austin, TX"
								/>
							</div>
							<div>
								<Label htmlFor="evalPortfolio" className="mb-1.5 block">Portfolio</Label>
								<Select
									id="evalPortfolio"
									value={newPortfolioId}
									onChange={(e) => {
										setNewPortfolioId(e.target.value)
										// Drop a stale conflict banner — it refers to the portfolio
										// the user just moved away from.
										setCreateState({ kind: 'idle' })
									}}
								>
									<option value="">No portfolio</option>
									{portfolios.map((p: any) => (
										<option key={p._id} value={p._id}>
											{p.assetName}
										</option>
									))}
								</Select>
								{selectedPortfolio && (
									<p className="text-xs text-gray-500 mt-1.5">
										{selectedPortfolioLocationCount} location
										{selectedPortfolioLocationCount === 1 ? '' : 's'} will be added as sites.
									</p>
								)}
							</div>

							{createState.kind === 'conflict' && (
								<div className="rounded-md border border-amber-200 bg-amber-50 p-3">
									<p className="text-sm text-amber-900">
										An evaluation already exists for this portfolio
										{createState.evaluationName ? `: "${createState.evaluationName}"` : ''}.
									</p>
									<p className="text-xs text-amber-700 mt-1">
										Open it, or choose a different portfolio to create a new evaluation.
									</p>
									<Button
										variant="primary"
										isOutline
										className="mt-2"
										onClick={() => handleOpenCreated(createState.evaluationId)}
									>
										Open Existing
									</Button>
								</div>
							)}

							{createState.kind === 'seedWarning' && (
								<div className="rounded-md border border-amber-200 bg-amber-50 p-3">
									<p className="text-sm text-amber-900">
										"{newName}" was created, but seeding sites from the portfolio failed:{' '}
										{createState.message}
									</p>
									<p className="text-xs text-amber-700 mt-1">
										You can retry from the evaluation's Sites tab.
									</p>
									<Button
										variant="primary"
										className="mt-2"
										onClick={() => handleOpenCreated(createState.evaluationId)}
									>
										Open Evaluation
									</Button>
								</div>
							)}

							{createState.kind === 'error' && (
								<p className="text-sm text-destructive">{createState.message}</p>
							)}

							<div className="flex justify-end gap-2 pt-2">
								<Button variant="ghost" onClick={resetAndClose}>Cancel</Button>
								<Button
									variant="primary"
									onClick={handleCreate}
									disabled={!newName.trim() || createState.kind === 'submitting'}
								>
									{createState.kind === 'submitting' ? 'Creating...' : 'Create'}
								</Button>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default Evaluations_IndexCN
